/* global Office, process, setTimeout, window */

import * as React from "react";
import axios, { AxiosResponse } from "axios";
import { useEffect, useRef, useState } from "react";
import {
  Button,
  Field,
  tokens,
  makeStyles,
  SelectionEvents,
  OptionOnSelectData,
  Spinner,
} from "@fluentui/react-components";
import { Open16Regular } from "@fluentui/react-icons";
import { getClient, getRunEvents, getTriggerStatus, postTrigger, searchClients } from "../../api/MiddleTier";
import { getMiddletierToken } from "../../helpers/middletierToken";
import {
  ASYNC_TRIGGER,
  DoneRun,
  OpenRunState,
  RunStatus,
  fromSentence,
  settle,
  waitForRun,
  withRetries,
} from "../../helpers/asyncRun";
import { connectRunEvents } from "../../helpers/runEvents";
import { ClientSearch } from "./ClientSearch";
import { Client } from "../models/Client";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../helpers/dateFormat";
import { fitAttachments } from "../../helpers/attachmentCap";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";

const useStyles = makeStyles({
  instructions: {
    fontWeight: tokens.fontWeightSemibold,
    marginTop: "20px",
    marginBottom: "10px",
  },
  error: {
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorStatusDangerBorder1,
    marginBottom: "10px",
  },
  warning: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorStatusWarningForeground1,
    marginBottom: "10px",
  },
  textPromptAndInsertion: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  textAreaField: {
    marginLeft: "20px",
    marginTop: "30px",
    marginBottom: "20px",
    marginRight: "20px",
  },
});

// Returned by fetchClientsByEmailAddress when the user cannot authenticate, so callers
// stop retrying with other email addresses.
const AUTH_FAILED = -1;

interface PolicyClient {
  clientName: string;
  clientId: string;
}

/** What ended an async run (#636), as the pane shows it. */
type RunProblem =
  | { key: "timeout" | "error" | "busy" | "tooLarge" | "tooMany" | "anotherUser" }
  | { key: "gaveUp"; runId: string };

/** A done run waiting for a click on Open in GOS: the browser blocked GOS, or a reopened pane found the run. */
interface ReadyRun {
  run: DoneRun;
  blocked: boolean;
}

/** The mailID the service keys a mail by: its Message-ID's local part, lower-cased. */
const currentMailId = () => Office.context.mailbox.item.internetMessageId.toLowerCase().split("@")[0].substring(1);

const ClaimCreation: React.FC = () => {
  const { t } = useTranslation();
  const appInsights = useAppInsightsContext();
  const [clientName, setClientName] = useState<string | undefined>(undefined);
  const [clientID, setClientID] = useState<string | undefined>(undefined);
  const [isOrganization, setIsOrganization] = useState<boolean>(true);
  const [clients, setClients] = useState<Client[]>([]);
  const [claimCreation, setClaimCreation] = useState<boolean>(false);
  const [fetchingClients, setFetchingClients] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [authError, setAuthError] = useState<string | undefined>(undefined);
  const [country, setCountry] = useState<number | undefined>(undefined);
  const [internalNumber, setInternalNumber] = useState<string | undefined>(undefined);
  const [triggerError, setTriggerError] = useState<string | undefined>(undefined);
  // The attachments the last Create left out over the size cap (#708).
  const [leftOutAttachments, setLeftOutAttachments] = useState<string[]>([]);
  // The attachments Office could not read for the last Create: not sent either.
  const [unreadableAttachments, setUnreadableAttachments] = useState<string[]>([]);
  // How many attachments the last Create sent: the pane says what was processed.
  const [sentAttachments, setSentAttachments] = useState(0);
  const [policyClient, setPolicyClient] = useState<PolicyClient | undefined>(undefined);
  // The full record for the client the Policy API detected. The policy response
  // carries only a name and an id (verified against the live API: it returns
  // clientName and clientPartnerId and no partner type or internal number), but
  // filing against that client needs its partner type for the GOS URL and its
  // internal number for the Fleets lookup. Both come from the client search,
  // matched on ID rather than name so there is no ambiguity. (#513)
  const [policyClientRecord, setPolicyClientRecord] = useState<Client | undefined>(undefined);
  const [resolvingPolicyClient, setResolvingPolicyClient] = useState<boolean>(false);
  const [differentPolicyClientWarning, setDifferentPolicyClientWarning] = useState<boolean>(false);
  const [policyClientReprocessFailed, setPolicyClientReprocessFailed] = useState<boolean>(false);
  /** The client the STORED extraction was produced under - see the mismatch branch. */
  const [extractedUnderClientId, setExtractedUnderClientId] = useState<string | undefined>(undefined);
  // The async trigger (#636). Never set while REACT_APP_ASYNC_TRIGGER is off.
  const [runProgress, setRunProgress] = useState<{ status: OpenRunState; slow: boolean } | undefined>(undefined);
  const [runProblem, setRunProblem] = useState<RunProblem | undefined>(undefined);
  const [readyRun, setReadyRun] = useState<ReadyRun | undefined>(undefined);
  /** Bumped to stop the wait in flight: by a new run, and when the pane closes. */
  const waitGeneration = useRef(0);
  const closed = useRef(false);
  /** Set once every client lookup, the mail-body scan included, is over. */
  const [clientLookupDone, setClientLookupDone] = useState<boolean>(false);
  const lookedForRun = useRef(false);
  /** Whether the user has picked a client, and the client selected now: read by a wait that outlives its render. */
  const userPicked = useRef(false);
  const selectedClientId = useRef<string | undefined>(undefined);
  selectedClientId.current = clientID;
  /** The reopened pane's latest-run lookup while it is pending: Create waits for its token request. */
  const runLookup = useRef<Promise<unknown>>(Promise.resolve());

  const fetchClientsByEmailAddress = async (emailAddress: string): Promise<number> => {
    setError(undefined);
    let middletierToken: string;
    try {
      middletierToken = await getMiddletierToken();
    } catch (err: any) {
      // SSO and fallback dialog both failed (e.g. 13001/13002, or the user closed the login dialog).
      // Without this, the spinner never stops and /client is never called.
      appInsights.trackException({
        error: new Error(`Middle tier token failed: ${err?.code ?? ""} ${err?.message ?? err}`),
      });
      setAuthError(`${err?.code ?? err?.message ?? "unknown"}`);
      setFetchingClients(false);
      return AUTH_FAILED;
    }
    const formData = new FormData();
    formData.append("emailAddress", emailAddress);
    try {
      let response: any = await getClient(middletierToken, formData);
      if (!response) {
        throw new Error("Middle tier didn't respond");
      } else if (response.claims) {
        // Microsoft Graph requires an additional form of authentication. Have the Office host
        // get a new token using the Claims string, which tells AAD to prompt the user for all
        // required forms of authentication.
        let mfaMiddletierToken: string = await getMiddletierToken({ authChallenge: response.claims });
        response = await getClient(mfaMiddletierToken, formData);
      }

      setFetchingClients(false);
      let res: Client[] = response.data;
      setClients(res);
      if (res.length === 0) return 0;
      setClientName(res[0].clientName);
      setClientID(`${res[0].clientId}`);
      setIsOrganization(res[0].isOrganization);
      setCountry(res[0].countryCodeId);
      setInternalNumber(`${res[0].internalNumber}`);
      return res.length;
    } catch (err: any) {
      setFetchingClients(false);
      appInsights.trackException({ error: err instanceof Error ? err : new Error(String(err)) });
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        setAuthError(`HTTP ${status}`);
        return AUTH_FAILED;
      }
      setError(err.message);
      return 0;
    }
  };

  // Resolves once the scan is over, the lookups it started included: the async
  // trigger's latest-run lookup waits for it, since Office refuses a token request
  // while another is pending (#636).
  const fetchClientsFromEmailBody = () =>
    new Promise<void>((resolve) => {
      try {
        Office.context.mailbox.item.getAllInternetHeadersAsync(async (result: Office.AsyncResult<string>) => {
          // Resolved here unless the body's own callback took that over: an exception
          // before it (or a getAsync that throws) must not leave the scan pending forever.
          let handedOn = false;
          try {
            const referencesPattern = /References:\s*.*<([^>]+)>/im;
            if (referencesPattern.test(result.value)) {
              appInsights.trackTrace({ message: "Email is a reply." });
              Office.context.mailbox.item.body.getAsync(Office.CoercionType.Text, async (mailResult) => {
                try {
                  if (mailResult.status !== Office.AsyncResultStatus.Succeeded) {
                    appInsights.trackException({ error: new Error("Failed to get mail body.") });
                  } else {
                    // If email has been forwarded, the "from" field will contain the original sender's email address
                    // In this case, the email address will be in the format "From: <email>"
                    // We need to extract the email address from this string
                    // It is also possible that the email message header is in a different language
                    const pattern =
                      /(From|Nga|Od|De |De la|Da|Von|Από|Feladó|Iš|Van|Vun|No|От|Од|Від|გამომგზავნი|Kimden|Кімнен|Saatja|Pošiljatelj|Pošiljalac|Fra):\s.*<([^>]+)>/g;
                    const matches = [...mailResult.value.matchAll(pattern)];
                    if (matches.length > 0) {
                      // Use the last match to get the email address of the original sender (thread initiator)
                      let matchesIterationIndex = 0;
                      let matchesCount = 0;
                      while (!matchesCount && matchesIterationIndex < matches.length) {
                        matchesCount = await fetchClientsByEmailAddress(matches[matchesIterationIndex++][2]);
                      }
                      appInsights.trackTrace({ message: `Email address extracted from the body.` });
                    } else {
                      appInsights.trackTrace({ message: `Email address not found in the body.` });
                    }
                  }
                } finally {
                  resolve();
                }
              });
              handedOn = true;
            } else {
              appInsights.trackTrace({ message: "Email is not a reply." });
            }
          } finally {
            if (!handedOn) resolve();
          }
        });
      } catch (err: any) {
        setFetchingClients(false);
        setError(err.message);
        resolve();
      }
    });

  const fetchClients = async () => {
    let bodyScan: Promise<void> = Promise.resolve();
    try {
      const senderEmail = Office.context.mailbox.item.from?.emailAddress;
      const matchesCount = senderEmail ? await fetchClientsByEmailAddress(senderEmail) : 0;
      if (matchesCount === 0) {
        bodyScan = fetchClientsFromEmailBody();
      }
    } catch (err: any) {
      appInsights.trackException({ error: err instanceof Error ? err : new Error(String(err)) });
      setError(err?.message);
    } finally {
      setFetchingClients(false);
    }
    // Bounded: an Office callback that never comes must not hold the latest-run lookup forever.
    await Promise.race([bodyScan, new Promise((resolve) => setTimeout(resolve, 30000))]);
    setClientLookupDone(true);
  };

  useEffect(() => {
    // Get the client list
    fetchClients();
  }, []);

  useEffect(() => {
    // Derived, not accumulated. The earlier `policyClient && ...` form left the
    // warning standing after `policyClient` was cleared on adoption, and the
    // warning branch below reads `policyClient.clientName` - so the pane
    // crashed on the render right after a SUCCESSFUL hand-off. (#513)
    setDifferentPolicyClientWarning(!!policyClient && policyClient.clientId !== clientID);
  }, [policyClient, clientID]);

  useEffect(() => {
    if (!policyClient) {
      setPolicyClientRecord(undefined);
      setResolvingPolicyClient(false);
      return undefined;
    }
    let cancelled = false;

    const resolve = async () => {
      // Often free: the detected client is frequently already among the
      // suggestions fetched for this mail.
      const known = clients.find((c) => `${c.clientId}` === policyClient.clientId);
      if (known) {
        // Normalised, NOT stored raw. The BFF declares `public long ClientId`
        // and registers no converter, so /api/client serves these ids as JSON
        // NUMBERS - while `Client` declares `clientId: string`, so nothing the
        // compiler sees is wrong. The search path below already restringifies;
        // this path did not, and the id then flowed into the strict compare
        // against the service's (string) answer, which was therefore ALWAYS
        // unequal: every correction taken from the suggestion list re-filed the
        // blob and then told the operator nothing had changed. (#513)
        setPolicyClientRecord({ ...known, clientId: `${known.clientId}` });
        // A run still in flight for a PREVIOUS policyClient has its `finally`
        // skipped by `cancelled`, so this synchronous path is the only thing
        // left that can clear the flag - and a stuck `true` disables the
        // button permanently.
        setResolvingPolicyClient(false);
        return;
      }
      setResolvingPolicyClient(true);
      try {
        // Background lookup: never opens the login dialog on its own.
        const token: string = await getMiddletierToken({ interactive: false });
        let response = await searchClients(token, policyClient.clientName);
        // The middle tier answers HTTP 200 carrying `{claims}` when the
        // on-behalf-of exchange needs step-up auth (search-get/index.js).
        // Reading `results` off that object yields undefined, so the button
        // would silently never appear for exactly the users who need consent -
        // and the catch below would not fire either, because nothing threw.
        // `fetchClientsByEmailAddress` already handles this shape; so must this.
        const challenge = (response.data as unknown as { claims?: string })?.claims;
        if (challenge) {
          const steppedUpToken: string = await getMiddletierToken({ authChallenge: challenge, interactive: false });
          response = await searchClients(steppedUpToken, policyClient.clientName);
        }
        // Match on the ID. Searching by name is only how the BFF is queried;
        // two clients can share a name, and picking the wrong one here would
        // file the claim against a third party entirely.
        const hit = (response.data?.results ?? []).find(
          ({ document }) => `${document.partnerID}` === policyClient.clientId
        );
        if (cancelled) return;
        setPolicyClientRecord(
          hit
            ? {
                clientId: `${hit.document.partnerID}`,
                clientName: hit.document.partnerName,
                internalNumber: hit.document.partnerNumber,
                isOrganization: hit.document.partnerType
                  .map((partnerType) => `${partnerType}`)
                  .includes(process.env.REACT_APP_BUSSINESS_OBJECT_TYPE_COMPANY_CLIENT),
                countryCodeId: hit.document.countryCodeID,
                address: "",
              }
            : undefined
        );
      } catch (err: any) {
        if (!cancelled) setPolicyClientRecord(undefined);
        appInsights.trackException({ error: err instanceof Error ? err : new Error(String(err)) });
      } finally {
        if (!cancelled) setResolvingPolicyClient(false);
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [policyClient, clients]);

  /**
   * `override` exists because the policy-client path opens GOS in the same tick
   * it adopts the client: setting state and reading it back here would still
   * see the old value.
   *
   * It carries `isOrganization` as well as the id, and that is not optional.
   * The query-parameter NAME is chosen from it - `organisationoid` for a
   * company, `partneroid` for a person - so overriding the id alone would send
   * the adopted client's id under the previous client's partner type, and GOS
   * would resolve it against the wrong kind of entity, or not at all. (#513)
   */
  const openGosClaims = (override?: { clientId: string; isOrganization: boolean }) => {
    const gosClientId = override?.clientId ?? clientID;
    const gosIsOrganization = override?.isOrganization ?? isOrganization;
    const messageId = Office.context.mailbox.item.internetMessageId.toLowerCase().split("@")[0].substring(1);
    // The window, or null when the browser blocked it: the async path offers a button then (#636).
    return window.open(
      // Encoded: `messageId` comes from the Message-ID header, which the
      // SENDER writes. An `&` in it appends a parameter of the sender's
      // choosing - including a second partner id, which most parsers resolve
      // last-wins. The receiver decodes, so the value delivered is unchanged.
      `${process.env.REACT_APP_BASE_URL_GOS}/claim?isNewClaim=true&${
        gosIsOrganization ? "organisationoid" : "partneroid"
      }=${encodeURIComponent(gosClientId)}&messageid=${encodeURIComponent(messageId)}&mailRestId=${encodeURIComponent(
        Office.context.mailbox.convertToRestId(Office.context.mailbox.item.itemId, Office.MailboxEnums.RestVersion.v2_0)
      )}`, // Remark: Consider using different method for mobile devices since convertToRestId is not supported
      "_blank",
      "location=yes,height=570,width=520,scrollbars=yes,status=yes"
    );
  };

  /**
   * Today's client check on a done async run, then GOS (#636). `sentClientId` is the
   * client the run was asked for, as the synchronous path compares the client it sent.
   * GOS opens after the wait, outside the click, so a browser may block it: then the
   * pane offers a button (Decision 4).
   */
  const finishRun = (run: DoneRun, sentClientId: string | undefined, clicked = false) => {
    setReadyRun(undefined);
    if (run.clientPartnerId !== null && sentClientId !== run.clientPartnerId) {
      setExtractedUnderClientId(sentClientId);
      setPolicyClientReprocessFailed(false);
      setPolicyClient({ clientName: run.clientName ?? run.clientPartnerId, clientId: run.clientPartnerId });
      return;
    }
    // After a click it is offered no more: a host that opens the window without
    // returning it would otherwise offer, and open, it again and again.
    if (!openGosClaims() && !clicked) setReadyRun({ run, blocked: true });
  };

  /**
   * Offers GOS for a done run this pane did not start. GOS opens with the client the
   * extraction was made for, so the pane selects the client the run was asked for. A run
   * whose client is not among this mail's clients, or an older run that does not say, is
   * not offered; nor is one for another client than the one the user has picked meanwhile,
   * whose choice it would silently undo.
   */
  const offerRun = (run: DoneRun) => {
    const client = clients.find((c) => `${c.clientId}` === run.requestedClientId);
    if (!client) return;
    if (userPicked.current && selectedClientId.current !== run.requestedClientId) return;
    setClientName(client.clientName);
    setClientID(`${client.clientId}`);
    setIsOrganization(client.isOrganization);
    setCountry(client.countryCodeId);
    setInternalNumber(client.internalNumber ? `${client.internalNumber}` : undefined);
    setReadyRun({ run, blocked: false });
  };

  /**
   * Reads a run's status with one token, fetched again only when it is refused (an
   * expired token). The socket's URL (#716) takes the same token, and a fresh one is
   * asked of Office once for both: two requests at a time are refused (13008).
   */
  const statusReader = (mailID: string, token?: string, isCancelled?: () => boolean) => {
    let current = token;
    let asking: Promise<string> | undefined;
    // A token that cannot be had, asked a few times (Office refuses one while another
    // request is pending, 13008), ends the wait as a refusal does: asked every 500 ms,
    // it would only ask Office again and again. The pane then offers Check again (#665).
    const freshToken = () => {
      if (!asking) {
        asking = (async () => {
          try {
            current = await withRetries(
              () => getMiddletierToken({ interactive: false }),
              undefined,
              undefined,
              isCancelled
            );
            return current;
          } catch {
            throw Object.assign(new Error("No token for the run status."), { response: { status: 401 } });
          } finally {
            asking = undefined;
          }
        })();
      }
      return asking;
    };
    // A refresh on its way wins over the token it replaces: the socket's URL asked
    // meanwhile would get a token the middle tier no longer takes.
    const tokenNow = (): Promise<string> => asking ?? (current !== undefined ? Promise.resolve(current) : freshToken());
    const read = async (runId?: string): Promise<RunStatus> => {
      // Outside the try: no token at all is the refusal itself, never asked for again here.
      const first = await tokenNow();
      try {
        return (await getTriggerStatus(first, mailID, runId)).data;
      } catch (error: any) {
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          current = await freshToken();
        } else if (status === undefined && !isCancelled?.()) {
          // No answer the pane can read: APIM's own 401 has no CORS header (#703), so in
          // staging and production an expired token looks like a network error. Asked
          // again with a fresh token; one that cannot be had leaves it a failed poll.
          try {
            current = await freshToken();
          } catch {
            throw error;
          }
        } else {
          throw error;
        }
        return (await getTriggerStatus(current, mailID, runId)).data;
      }
    };
    return { read, token: tokenNow };
  };

  /** Waits for a run, showing its progress. Anything but done becomes the problem the pane shows. */
  const waitFor = async (mailID: string, runId: string, token?: string): Promise<DoneRun | undefined> => {
    // A pane closed while the run was being accepted starts no wait.
    if (closed.current) return undefined;
    const generation = ++waitGeneration.current;
    const stillWaiting = () => generation === waitGeneration.current;
    const reader = statusReader(mailID, token, () => !stillWaiting());
    // The status pushes (#716): the wait reads when one says the run changed. Without
    // them (off, refused, or a socket that keeps failing) it polls, as before.
    const events = connectRunEvents({
      clientUrl: async () => {
        const url = (await getRunEvents(await reader.token())).data?.url;
        if (typeof url !== "string" || !url.startsWith("wss://")) throw new Error("No run events URL.");
        return url;
      },
      // A fixed reason only: the URL, which an error would carry, holds the token.
      onClosedForGood: (reason) => appInsights.trackTrace({ message: `Async run events off (${reason}); polling.` }),
    });
    try {
      const outcome = await waitForRun({
        runId,
        fetchStatus: reader.read,
        events,
        // The same status again is the same state: no render every 500 ms.
        onProgress: (progress) =>
          stillWaiting() &&
          setRunProgress((shown) =>
            shown && shown.status === progress.status && shown.slow === progress.slow ? shown : progress
          ),
        isCancelled: () => !stillWaiting(),
      });
      if (outcome.kind === "done") return outcome;
      if (outcome.kind === "failed") {
        appInsights.trackTrace({ message: `Async run ended failed (${outcome.errorKey}).` });
        setRunProblem({ key: outcome.errorKey === "timeout" || outcome.errorKey === "stale" ? "timeout" : "error" });
      } else if (outcome.kind === "anotherUser") {
        setRunProblem({ key: "anotherUser" });
      } else if (outcome.kind === "gaveUp") {
        if (outcome.reason === "auth") {
          appInsights.trackTrace({ message: "Async run status refused (auth); offering Check again." });
        }
        setRunProblem({ key: "gaveUp", runId: outcome.runId });
      }
      return undefined;
    } finally {
      events.close();
      if (stillWaiting()) setRunProgress(undefined);
    }
  };

  /**
   * Check again is a click, so its token may be asked for with a sign-in: a user whose
   * silent sign-in lapsed, which ended the wait, can sign in again (#665). Without a
   * token the wait asks silently, as before.
   */
  const checkAgain = async (runId: string) => {
    // Busy at once: a second click, or Try again, while the sign-in is pending would ask
    // Office for another token (13008).
    setRunProblem(undefined);
    setClaimCreation(true);
    let token: string | undefined;
    try {
      token = await getMiddletierToken();
    } catch {
      token = undefined;
    }
    resumeRun(currentMailId(), runId, token);
  };

  /** Follows a run this pane did not start (a reopened pane, or Check again): when it is done, GOS is a click away. */
  const resumeRun = async (mailID: string, runId: string, token?: string) => {
    setRunProblem(undefined);
    setClaimCreation(true);
    try {
      const run = await waitFor(mailID, runId, token);
      if (run) offerRun(run);
    } finally {
      setClaimCreation(false);
    }
  };

  /**
   * The async trigger (#636): cnp-service stores the mail and queues the run, and the
   * pane waits on its status. Every failure ends here, as a problem the pane shows.
   */
  const createClaimAsync = async (
    formData: FormData,
    mailID: string,
    middletierToken: string,
    sentClientId: string | undefined
  ) => {
    formData.append("async", "1");
    let answer: AxiosResponse;
    try {
      answer = await postTrigger(middletierToken, formData);
    } catch (caught: any) {
      appInsights.trackException({ error: caught instanceof Error ? caught : new Error(String(caught)) });
      const status = caught?.response?.status;
      const refusal = caught?.response?.data?.error;
      if (status !== 503 || refusal !== "async_unavailable") {
        setRunProblem({
          key:
            status === 413
              ? "tooLarge"
              : status === 429 && refusal === "too_many_runs"
              ? "tooMany"
              : status === 503 && refusal === "busy"
              ? "busy"
              : "error",
        });
        return;
      }
      // This cnp-service has no runs queue: the synchronous path still works.
      formData.delete("async");
      try {
        answer = await postTrigger(middletierToken, formData);
      } catch (again: any) {
        appInsights.trackException({ error: again instanceof Error ? again : new Error(String(again)) });
        // As the synchronous path reads it: a 500 is the request's time running out.
        setRunProblem({ key: again?.response?.status === 500 ? "timeout" : "error" });
        return;
      }
    }
    if (answer.status !== 202) {
      // Run synchronously, by a backend without the async path: today's answer.
      const ran = fromSentence(answer.data);
      if (ran) finishRun(ran, sentClientId);
      else setRunProblem({ key: "error" });
      return;
    }
    const runId = answer.data?.runId;
    if (typeof runId !== "string" || !runId) {
      setRunProblem({ key: "error" });
      return;
    }
    const run = await waitFor(mailID, runId, middletierToken);
    if (run) finishRun(run, sentClientId);
  };

  // A reopened pane asks for the user's latest run of the mail (#636): a run still
  // going is followed, a done one offers GOS. Asked once every client lookup is over,
  // because Office refuses a token request while another is pending.
  useEffect(() => {
    if (!ASYNC_TRIGGER || !clientLookupDone || lookedForRun.current) return;
    lookedForRun.current = true;
    const generation = waitGeneration.current;
    const mailID = currentMailId();
    // Asked again when Office refuses it (13008): a run still going must not be missed,
    // or the pane would offer Create and start a second one (#665).
    runLookup.current = withRetries(
      () => getMiddletierToken({ interactive: false }),
      undefined,
      undefined,
      () => generation !== waitGeneration.current
    )
      .then(async (token) => {
        const answer = await statusReader(mailID, token).read();
        // A run started in this pane meanwhile wins.
        if (generation !== waitGeneration.current) return;
        const next = settle(answer);
        if (next.kind === "done") offerRun(next);
        // The wait goes on with the lookup's token: one token request fewer.
        else if (next.kind === "poll") resumeRun(mailID, next.runId, token);
        // A failed run, or one another user replaced: nothing to offer.
      })
      // No run of this user's (404), or none readable: the pane starts as usual.
      .catch(() => undefined);
  }, [clientLookupDone]);

  // A closed pane stops its wait.
  useEffect(() => {
    closed.current = false;
    return () => {
      closed.current = true;
      waitGeneration.current++;
    };
  }, []);

  /**
   * Adopt the client the Policy API detected, without paying for the mail again.
   *
   * The alternative the UI used to leave - retype the client in the dropdown and
   * press Create again - re-runs the whole extraction: every model call and any
   * OCR, to change only the policy-derived fields. Nothing the model produced
   * depends on which client the claim is filed under.
   *
   * Everything client-scoped comes from `policyClientRecord`, never from the
   * currently selected client: the internal number drives the Fleets lookup and
   * the partner type drives the GOS query-parameter name, and sending the
   * previous client's values with the new client's id would file the claim
   * against one client's policy and another's fleet. (#513)
   */
  const handleUsePolicyClient = () => {
    if (!policyClientRecord) return;
    const adopted = policyClientRecord;
    setClaimCreation(true);
    setPolicyClientReprocessFailed(false);
    const messageId = Office.context.mailbox.item.internetMessageId.toLowerCase().split("@")[0].substring(1);
    const formData = new FormData();
    formData.append("mailID", messageId);
    formData.append("subject", Office.context.mailbox.item.subject);
    formData.append("date", formatDate(Office.context.mailbox.item.dateTimeCreated));
    formData.append("clientID", adopted.clientId);
    formData.append("policyOnly", "1");
    // The client being corrected AWAY from. cnp-service needs it to refuse the
    // one correction it cannot carry out: a client whose own rules overwrote an
    // extracted value in place (SPAR replaces Claim_location and the address the
    // model found is not kept) cannot be corrected to a client without them.
    extractedUnderClientId && formData.append("previousClientID", extractedUnderClientId);
    adopted.countryCodeId !== undefined && formData.append("clientCountry", `${adopted.countryCodeId}`);
    adopted.internalNumber && formData.append("clientInternalNumber", `${adopted.internalNumber}`);
    // The body still goes: it is small next to the attachments, and the subline
    // classification inside the policy lookup reads it. The attachments - up to
    // 40 MB, and the OCR - are deliberately NOT sent.
    Office.context.mailbox.item.body.getAsync(Office.CoercionType.Text, async (mailResult) => {
      if (mailResult.status !== Office.AsyncResultStatus.Succeeded) {
        // Abort, exactly as the full path does. Sending "" here was not a
        // harmless degradation: the subline classifier inside the policy lookup
        // reads this text, so the claim would be re-filed under a line chosen
        // from the SUBJECT alone - and the blob overwritten - while the
        // operator was told it had succeeded. (#513)
        appInsights.trackException({ error: new Error("Failed to get mail body.") });
        setPolicyClientReprocessFailed(true);
        setClaimCreation(false);
        return;
      }
      formData.append("body", mailResult.value);
      try {
        const middletierToken: string = await getMiddletierToken();
        const reprocessed = await axios({
          method: "POST",
          url: `${process.env.REACT_APP_BASE_URL_API}/trigger`,
          headers: {
            "X-Custom-Authorization": `Bearer ${middletierToken}`,
            "Content-Type": "multipart/form-data",
          },
          data: formData,
        });
        // The service answers HTTP 200 whether or not it resolved anything -
        // a lookup that found nothing, and a Policy or Fleets API that was
        // simply down, both come back as `clientPartnerId: None`. Handing off
        // on that would open GOS on an extraction that was NOT corrected,
        // with nothing on screen to say so. Same parse the full path uses.
        const resolvedPartnerId = `${reprocessed.data}`.slice(12).split(", clientPartnerId: ")[1];
        if (resolvedPartnerId !== adopted.clientId) {
          setPolicyClientReprocessFailed(true);
          return;
        }
        // Adopt it in the UI too. Without this the warning branch re-renders
        // with the OLD client still selected, and a subsequent "Create anyway"
        // would hand off that client against a blob now re-resolved for this
        // one - two clients in one claim.
        setClientID(adopted.clientId);
        setClientName(adopted.clientName);
        setIsOrganization(adopted.isOrganization);
        setCountry(adopted.countryCodeId);
        // Not `${adopted.internalNumber}` unguarded: a record with no partner
        // number would store the literal string "undefined", which is truthy,
        // and the next ordinary Create would send it - building a Fleets URL
        // ending /clientsvehicles/undefined/W123AB. The FormData append above
        // already guards this; state must too.
        setInternalNumber(adopted.internalNumber ? `${adopted.internalNumber}` : undefined);
        setPolicyClient(undefined);
        openGosClaims({ clientId: adopted.clientId, isOrganization: adopted.isOrganization });
      } catch (caught: any) {
        const processedError = caught instanceof Error ? caught : new Error(String(caught));
        appInsights.trackException({ error: processedError });
        // Deliberately NOT setTriggerError. That branch renders before the
        // warning block and is never cleared, so a transient failure of the
        // OPTIONAL correction removed the whole block - including "create
        // anyway", which was still perfectly valid - and left the operator with
        // an error message and no way forward at all. A failure here must cost
        // the operator the correction, not the option they already had. (#513)
        setPolicyClientReprocessFailed(true);
      } finally {
        setClaimCreation(false);
      }
    });
  };

  const handleClaimCreate = () => {
    setClaimCreation(true);
    // A new run: what the last async run left on screen goes, and a wait still in flight stops.
    waitGeneration.current++;
    setRunProblem(undefined);
    setReadyRun(undefined);
    setLeftOutAttachments([]);
    setUnreadableAttachments([]);
    setSentAttachments(0);
    const formData = new FormData();
    const messageId = Office.context.mailbox.item.internetMessageId.toLowerCase().split("@")[0].substring(1);
    formData.append("mailID", messageId);
    formData.append("subject", Office.context.mailbox.item.subject);
    formData.append("date", formatDate(Office.context.mailbox.item.dateTimeCreated));
    formData.append("clientID", clientID || "");
    country && formData.append("clientCountry", `${country}`);
    internalNumber && formData.append("clientInternalNumber", internalNumber);
    Office.context.mailbox.item.body.getAsync(Office.CoercionType.Text, async (mailResult) => {
      if (mailResult.status !== Office.AsyncResultStatus.Succeeded) {
        appInsights.trackException({ error: new Error("Failed to get mail body.") });
        setClaimCreation(false);
      } else {
        formData.append("body", mailResult.value);
        const attachments = Office.context.mailbox.item.attachments;
        if (Office.context.mailbox.item.attachments !== undefined) {
          // Every attachment is read first, then they are sent in the mail's order
          // up to the cap (#708); Office answers in any order.
          const read: Array<{ name: string; size: number; blob: Blob } | undefined> = [];
          const unreadable: Array<string | undefined> = [];
          const attachmentDataReaders: Promise<void>[] = [];
          for (let i = 0; i < attachments.length; i++) {
            const attachment = attachments[i];
            if (attachment.isInline || !attachment.id) {
              continue;
            }

            attachmentDataReaders.push(
              new Promise((resolve) => {
                Office.context.mailbox.item.getAttachmentContentAsync(attachment.id, (contentResult) => {
                  // An unreadable attachment is skipped and named in the pane, and the
                  // others are still waited for: they are sent once every one is read.
                  if (contentResult.status !== Office.AsyncResultStatus.Succeeded) {
                    appInsights.trackException({ error: new Error("Failed to get attachment content.") });
                    unreadable[i] = attachment.name;
                    return resolve();
                  }

                  const attachmentData = contentResult.value;
                  const blob = new Blob([attachmentData.content], { type: attachmentData.format });
                  read[i] = { name: attachment.name, size: blob.size, blob };
                  resolve();
                });
              })
            );
          }
          try {
            await Promise.all(attachmentDataReaders);
          } catch (e) {
            appInsights.trackTrace({ message: "At least one attachment promise was rejected while reading content." });
            appInsights.trackException({
              error: e instanceof Error ? e : new Error("Attachment processing promise rejected"),
            });
          }
          // 40 MB of base64, under the 40 MiB cnp-service's async accept takes (#708).
          // The old 29 MB was the Static Web Apps request limit, which the add-in no
          // longer goes through.
          const { sent, leftOut } = fitAttachments(
            read.filter((item): item is { name: string; size: number; blob: Blob } => item !== undefined),
            Number(process.env.REACT_APP_MAX_ATTACHMENT_SIZE)
          );
          sent.forEach((item, index) => formData.append(`attachment${index}`, item.blob, item.name));
          if (leftOut.length) {
            appInsights.trackTrace({ message: `Attachments left out over the size cap: ${leftOut.length}.` });
          }
          setSentAttachments(sent.length);
          setLeftOutAttachments(leftOut);
          setUnreadableAttachments(unreadable.filter((name): name is string => name !== undefined));
        }

        try {
          // Office refuses a token request while the latest-run lookup's is pending.
          if (ASYNC_TRIGGER) await runLookup.current;
          const middletierToken = await getMiddletierToken();
          if (ASYNC_TRIGGER) {
            await createClaimAsync(formData, messageId, middletierToken, clientID);
            return;
          }
          const parseMailResult = await axios({
            method: "POST",
            url: `${process.env.REACT_APP_BASE_URL_API}/trigger`,
            headers: {
              "X-Custom-Authorization": `Bearer ${middletierToken}`,
              "Content-Type": "multipart/form-data",
            },
            data: formData,
          });
          const response = parseMailResult.data;
          const clientData = response.slice(12).split(", clientPartnerId: ");
          if (clientData[1] !== "None" && clientID !== clientData[1]) {
            // `extractedUnderClientId` is pinned HERE, not read from the picker
            // when the correction is submitted: the dropdown stays enabled
            // while the warning is up, so a later change would send a client
            // the extraction was never stored under - and that value is what
            // the service uses to refuse a correction it cannot reverse.
            setExtractedUnderClientId(clientID);
            // A newly detected client has not been attempted yet, so the
            // previous attempt's failure must not be shown against it.
            setPolicyClientReprocessFailed(false);
            setPolicyClient({ clientName: clientData[0], clientId: clientData[1] });
          } else {
            openGosClaims();
          }
        } catch (caught: any) {
          // Normalize the error without mutating the caught parameter (avoids no-param-reassign / immutability lint errors)
          let processedError: Error;
          if (caught?.response?.status === 500) {
            processedError = new Error(t("oac.triggerTimeout.error"));
          } else {
            processedError = caught instanceof Error ? caught : new Error(String(caught));
          }
          appInsights.trackException({ error: processedError });
          setTriggerError(processedError.message);
        } finally {
          setClaimCreation(false);
        }
      }
    });
  };

  /** What ended an async run, in words: each key has an English default until Phrase has it. */
  const problemText = (problem: RunProblem) => {
    switch (problem.key) {
      case "timeout":
        return t("oac.asyncRun.timeout", { defaultValue: "The claim took too long to process. Please try again." });
      case "busy":
        return t("oac.asyncRun.busy", {
          defaultValue: "Many claims are being processed right now. Please try again in a few minutes.",
        });
      case "tooLarge":
        return t("oac.asyncRun.tooLarge", { defaultValue: "This mail and its attachments are too large to process." });
      case "tooMany":
        // The per-user limit on runs (#643): far past normal use, so a script or a resend loop.
        return t("oac.asyncRun.tooMany", {
          defaultValue: "You have sent many claims in a short time. Please try again in a few minutes.",
        });
      case "anotherUser":
        return t("oac.asyncRun.anotherUser", {
          defaultValue: "Another user has started processing this mail since. Try again to process it for yourself.",
        });
      case "gaveUp":
        return t("oac.asyncRun.gaveUp", {
          defaultValue: "The claim is still being processed. Check again, or reopen this pane later.",
        });
      default:
        return t("oac.asyncRun.failed", { defaultValue: "The claim could not be processed. Please try again." });
    }
  };

  const styles = useStyles();

  return (
    <div className={styles.textPromptAndInsertion}>
      {fetchingClients ? (
        <Spinner size="large" />
      ) : authError ? (
        <Field className={`${styles.error} ${styles.textAreaField}`}>
          {t("auth.error.InternalError.txt")} ({authError})
        </Field>
      ) : (
        <>
          <Field
            className={styles.textAreaField}
            size="large"
            label={error || !clientName ? t("oac.client.error") : t("oac.client.result", { clientName })}
          >
            <ClientSearch
              options={clients}
              onOptionSelect={(__event: SelectionEvents, data: OptionOnSelectData) => {
                setClientName(data.optionText);
                const selectedClient = JSON.parse(data.optionValue);
                // Same hazard as the resolution effect above, and pre-existing:
                // the option carries the BFF's numeric id, so without this the
                // selected client's id is a number while every comparison and
                // every FormData field treats it as text. Line 108 already
                // stringifies the auto-selected one; this one did not, so a
                // manual pick made the mismatch check fire on a client that
                // actually matched.
                setClientID(selectedClient === undefined ? undefined : `${selectedClient.clientId}`);
                setIsOrganization(selectedClient?.isOrganization);
                setCountry(selectedClient.countryCodeId);
                setInternalNumber(selectedClient.internalNumber);
                setError(undefined);
                // A done run offered for GOS was made for the client picked before.
                setReadyRun(undefined);
                userPicked.current = true;
              }}
              disabled={claimCreation || !!triggerError}
              selectedKey={clientID}
            />
          </Field>
          {!triggerError && !differentPolicyClientWarning ? (
            <Field className={styles.instructions} role={ASYNC_TRIGGER ? "status" : undefined}>
              {!claimCreation
                ? t("oac.createClaim.description")
                : runProgress?.status === "queued"
                ? t("oac.asyncRun.queued", { defaultValue: "Waiting for the claim to be processed…" })
                : runProgress?.status === "running"
                ? t("oac.asyncRun.running", { defaultValue: "Reading the mail and its attachments…" })
                : t("oac.createClaim.actionProgress")}
            </Field>
          ) : null}
          {leftOutAttachments.length || unreadableAttachments.length ? (
            // What the claim is made from, what is not in it and why, and what is left to do (#708).
            <Field className={styles.warning} role="status">
              <div>
                <div>
                  {t("oac.attachments.processed", {
                    sent: sentAttachments,
                    total: sentAttachments + leftOutAttachments.length + unreadableAttachments.length,
                    defaultValue: "Processed: the mail and {{sent}} of {{total}} attachments.",
                  })}
                </div>
                {leftOutAttachments.length ? (
                  <div>
                    {t("oac.attachments.leftOut", {
                      names: leftOutAttachments.join(", "),
                      defaultValue: "Not processed, too large to send: {{names}}.",
                    })}
                  </div>
                ) : null}
                {unreadableAttachments.length ? (
                  <div>
                    {t("oac.attachments.unreadable", {
                      names: unreadableAttachments.join(", "),
                      defaultValue: "Not processed, Outlook could not read: {{names}}.",
                    })}
                  </div>
                ) : null}
                <div>
                  {t("oac.attachments.byHand", {
                    defaultValue: "Please add the data from the attachments not processed in GOS by hand.",
                  })}
                </div>
              </div>
            </Field>
          ) : null}
          {claimCreation && runProgress?.slow ? (
            <Field className={styles.warning} role="status">
              {t("oac.asyncRun.slow", {
                defaultValue:
                  "This is taking longer than usual. You can close this pane: the claim keeps processing, and reopening the pane picks it up.",
              })}
            </Field>
          ) : null}
          {!claimCreation && runProblem ? (
            <Field className={styles.error} role="alert">
              {problemText(runProblem)}
            </Field>
          ) : null}
          {!claimCreation && readyRun && !differentPolicyClientWarning ? (
            <>
              <Field className={styles.instructions}>
                {readyRun.blocked
                  ? t("oac.asyncRun.gosBlocked", { defaultValue: "If the claim form did not open, open it here." })
                  : t("oac.asyncRun.ready", { defaultValue: "The claim from your last run on this mail is ready." })}
              </Field>
              <Button
                appearance="primary"
                size="large"
                icon={<Open16Regular />}
                onClick={() => finishRun(readyRun.run, clientID, true)}
              >
                {t("oac.openInGOS.action")}
              </Button>
            </>
          ) : null}
          {triggerError ? (
            <Field className={styles.error}>{t("auth.error.InternalError.txt")}</Field>
          ) : differentPolicyClientWarning && policyClient ? (
            <>
              <Field className={styles.warning}>
                {t("oac.policyClient.warning", { policyClientName: policyClient.clientName })}
              </Field>
              {/*
                Both of these say out loud what the buttons below can only
                imply. Without them the operator meets a warning naming a
                client and, in one case, no way to act on it and no reason
                given - which reads as a half-loaded pane rather than an
                honest refusal. (#513)
              */}
              {policyClientReprocessFailed ? (
                <Field className={styles.error}>
                  {t("oac.usePolicyClient.failed", {
                    policyClientName: policyClient.clientName,
                    defaultValue:
                      "No policy could be found for {{policyClientName}}. Nothing was changed - create the claim with the selected client instead, or try again.",
                  })}
                </Field>
              ) : !policyClientRecord && !resolvingPolicyClient ? (
                <Field className={styles.warning}>
                  {t("oac.policyClient.unresolved", {
                    policyClientName: policyClient.clientName,
                    defaultValue:
                      "{{policyClientName}} could not be looked up, so the claim cannot be moved to them here.",
                  })}
                </Field>
              ) : null}
              {/*
                Only offered once the detected client is fully resolved. Without
                its partner type and internal number the claim would be filed
                against one client's policy and another's fleet, so when the
                lookup finds nothing the honest UI is to leave only the choice
                we can actually carry out. (#513)
              */}
              {(policyClientRecord || resolvingPolicyClient) && (
                <Button
                  appearance="primary"
                  size="large"
                  disabled={claimCreation || resolvingPolicyClient || !policyClientRecord}
                  icon={
                    claimCreation || resolvingPolicyClient ? (
                      <Spinner size="extra-tiny" appearance="inverted" />
                    ) : (
                      <Open16Regular />
                    )
                  }
                  onClick={handleUsePolicyClient}
                >
                  {t("oac.usePolicyClient.action", { defaultValue: "Create with the policy client" })}
                </Button>
              )}
              <Button
                appearance="secondary"
                size="large"
                disabled={claimCreation}
                icon={<Open16Regular />}
                onClick={() => openGosClaims()}
              >
                {t("oac.createAnyway.action")}
              </Button>
            </>
          ) : !claimCreation && runProblem?.key === "tooLarge" ? null : (
            <>
              {!claimCreation && runProblem?.key === "gaveUp" ? (
                <Button appearance="primary" size="large" onClick={() => checkAgain(runProblem.runId)}>
                  {t("oac.asyncRun.checkAgain", { defaultValue: "Check again" })}
                </Button>
              ) : null}
              {/* Also after giving up: a run stuck in the queue would otherwise hold the mail for 45 min. */}
              <Button
                appearance={readyRun || (!claimCreation && runProblem?.key === "gaveUp") ? "secondary" : "primary"}
                disabled={claimCreation || !clientName}
                size="large"
                icon={claimCreation ? <Spinner size="extra-tiny" appearance="inverted" /> : <Open16Regular />}
                onClick={handleClaimCreate}
              >
                {runProblem ? t("oac.asyncRun.retry", { defaultValue: "Try again" }) : t("oac.createClaim.action")}
              </Button>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default ClaimCreation;
